import React from "react";
import { Box, Typography, Select, Option, Stack } from "@mui/joy";
import { Building2, Plus, X, Ruler, Palette, Hash } from "lucide-react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { FormInput, Button, IconButton } from "../../../shared";

const CHIMNEY_TYPE_OPTIONS = [
    { value: "Brick", label: "Brick/Stone" },
    { value: "Siding", label: "Siding" },
] as const;

export function Chimneys({ loading }: { loading?: boolean }) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "Chimneys",
    });

    return (
        <Box
        >
            <Box mb={2}>
                <Typography level="title-md">Chimney</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify chimney types, sizes, colors, and counts.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Typography
                    level="body-xs"
                    sx={{
                        textTransform: "uppercase",
                        fontWeight: "xl",
                        letterSpacing: "0.5px",
                    }}
                >
                    Chimney List
                </Typography>
                <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{ mt: -0.5, mb: 1.5, fontSize: "12px" }}
                >
                    Add one or more chimneys.
                </Typography>

                <Stack spacing={1.5}>
                    {fields.map((item, index) => (
                        <Box
                            key={item.id}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: { xs: 1, sm: 0 },
                                mb: { xs: 0.5, sm: 0 },
                                pb: { xs: 1.5, sm: 0 },
                                borderBottom: {
                                    xs: "1px solid var(--standard-palette-divider)",
                                    sm: "none",
                                },
                                width: "100%",
                                minWidth: 0,
                            }}
                        >
                            {/* Type + mobile delete */}
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    width: { xs: "100%", sm: 175 },
                                    flexShrink: 0,
                                    minWidth: 0,
                                    alignItems: "stretch",
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Controller
                                        name={`Chimneys.${index}.Type`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                size="md"
                                                disabled={loading}
                                                onChange={(_, newValue) =>
                                                    field.onChange(newValue)
                                                }
                                                placeholder="Type"
                                                startDecorator={
                                                    <Building2 size={16} />
                                                }
                                                sx={{
                                                    bgcolor: "background.surface",
                                                    borderRadius: "4px",
                                                    boxShadow: "none",
                                                    width: "100%",
                                                    borderTopRightRadius: {
                                                        xs: "4px",
                                                        sm: 0,
                                                    },
                                                    borderBottomRightRadius: {
                                                        xs: "4px",
                                                        sm: 0,
                                                    },
                                                }}
                                            >
                                                {CHIMNEY_TYPE_OPTIONS.map(
                                                    (option) => (
                                                        <Option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </Option>
                                                    ),
                                                )}
                                            </Select>
                                        )}
                                    />
                                </Box>
                                <IconButton
                                    variant="soft"
                                    color="danger"
                                    disabled={loading}
                                    onClick={() => remove(index)}
                                    sx={{
                                        display: { xs: "flex", sm: "none" },
                                        height: "auto",
                                        alignSelf: "stretch",
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <X size={16} />
                                </IconButton>
                            </Box>

                            {/* Size */}
                            <Box
                                sx={{
                                    width: { xs: "100%", sm: 250 },
                                    flexShrink: 0,
                                    minWidth: 0,
                                }}
                            >
                                <Controller
                                    name={`Chimneys.${index}.Size`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            size="md"
                                            disabled={loading}
                                            onChange={(_, newValue) =>
                                                field.onChange(newValue)
                                            }
                                            placeholder="Size"
                                            startDecorator={<Ruler size={16} />}
                                            sx={{
                                                bgcolor: "background.surface",
                                                boxShadow: "none",
                                                width: "100%",
                                                borderRadius: {
                                                    xs: "4px",
                                                    sm: 0,
                                                },
                                                borderLeft: {
                                                    xs: undefined,
                                                    sm: 0,
                                                },
                                            }}
                                        >
                                            <Option value="None">None</Option>
                                            <Option value='Small (24"x24")'>
                                                Small (24"×24")
                                            </Option>
                                            <Option value='Average (32"x36")'>
                                                Average (32"×36")
                                            </Option>
                                            <Option value='Large (32"x60")'>
                                                Large (32"×60")
                                            </Option>
                                        </Select>
                                    )}
                                />
                            </Box>

                            {/* Color + Count + desktop delete */}
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: { xs: 1, sm: 0 },
                                    flex: 1,
                                    width: "100%",
                                    minWidth: 0,
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <FormInput
                                        name={`Chimneys.${index}.Color`}
                                        placeholder="Color"
                                        disabled={loading}
                                        startDecorator={<Palette size={16} />}
                                        sx={{
                                            borderRadius: {
                                                xs: "4px",
                                                sm: 0,
                                            },
                                            borderLeft: {
                                                xs: undefined,
                                                sm: 0,
                                            },
                                            minWidth: 0,
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        width: { xs: "36%", sm: 100 },
                                        flexShrink: 0,
                                        minWidth: 0,
                                    }}
                                >
                                    <FormInput
                                        name={`Chimneys.${index}.Count`}
                                        type="number"
                                        placeholder="Qty"
                                        disabled={loading}
                                        startDecorator={<Hash size={16} />}
                                        sx={{
                                            borderRadius: "4px",
                                            borderTopLeftRadius: {
                                                xs: "4px",
                                                sm: 0,
                                            },
                                            borderBottomLeftRadius: {
                                                xs: "4px",
                                                sm: 0,
                                            },
                                            borderLeft: {
                                                xs: undefined,
                                                sm: 0,
                                            },
                                            minWidth: 0,
                                        }}
                                    />
                                </Box>

                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="danger"
                                    disabled={loading}
                                    onClick={() => remove(index)}
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        mt: 0.5,
                                        ml: 1,
                                    }}
                                >
                                    <X size={20} />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}

                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<Plus size={16} />}
                        onClick={() =>
                            append({
                                Type: "Brick",
                                Size: 'Average (32"x36")',
                                Color: "",
                                Count: 1,
                            })
                        }
                        disabled={loading}
                        sx={{ alignSelf: "flex-start", mt: 0.5 }}
                    >
                        Add Chimney
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
